import clsx from 'clsx'
import {
  Activity,
  BookOpen,
  Bot,
  Brain,
  Command,
  FileText,
  IdCard,
  Lock,
  LogOut,
  Menu,
  Moon,
  Pin,
  PinOff,
  Plus,
  Search,
  ShieldCheck,
  Sun,
  Trash2,
  UsersRound,
  X
} from 'lucide-react'
import { type ReactNode, type RefObject, useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { brand } from '@/brand/active.ts'
import { BrandLockup } from '@/brand/BrandMarks.tsx'
import { NotificationsBell } from '@/components/marketplace/consumer/NotificationsBell.tsx'
import { highestRoleLabel } from '@/components/people/people-plane.ts'
import { CommandPalette } from '@/components/shell/CommandPalette.tsx'
import { groupConversations } from '@/components/shell/conversation-groups.ts'
import { Desk } from '@/components/shell/Desk.tsx'
import { HeaderSlotProvider } from '@/components/shell/header-slot.tsx'
import { installFocusRescue } from '@/components/ui/focus-rescue.ts'
import { useFocusTrap } from '@/components/ui/focus-trap.ts'
import { Badge, IconButton } from '@/components/ui/primitives.tsx'
import {
  useApprovals,
  useConversations,
  useDeleteConversation,
  useLogout,
  useSession,
  useUpdateConversation
} from '@/hooks/use-api.ts'
import { useSharingQueue } from '@/hooks/use-sharing.ts'
import { LanguageSwitcher, useI18n } from '@/i18n/index.ts'

/**
 * The room the product happens in.
 *
 * A warm desk under a slow aurora, with two frosted panels floating on it: a rail that holds
 * everything you have, and a stage that holds whatever you are doing. The desk shows through
 * the gap between them and around the edge, which is what stops the app reading as a set of
 * boxes bolted to a page.
 *
 * Below `lg` the rail leaves the flow and becomes a drawer over the stage. It stays mounted
 * either way — `display:none` rather than unmounted — so the menu button's `aria-controls`
 * always resolves and the drawer never has to rebuild its scroll position.
 *
 * Two keyboard shortcuts, both drawn in the design and both wired here rather than per page:
 * `/` opens the palette, `⌘K` goes to the screen a conversation starts on. Both are ignored
 * while a field has focus, because a slash inside the composer is a slash.
 *
 * **Two things here lead home, and neither of them creates anything** (PRD-19 NAV-001/002).
 * The wordmark goes to `/` because a wordmark always does, and "New conversation" goes to `/`
 * because that is the screen a conversation is composed on: the agent is chosen there, and the
 * pin it makes is permanent (AGM-007). This control used to POST a conversation and land on an
 * empty chat — which chose the agent for the person, and left a row titled "New conversation"
 * in their history every time they thought better of it. A conversation is now created by
 * asking a question, and by nothing else.
 */

const useTheme = () => {
  // Read from the DOM, which the pre-paint script in boot.js has already stamped. Reading
  // localStorage again here would be a second source of truth, and initialising to a constant
  // would overwrite a stored dark preference with light on the first effect.
  const [theme, setTheme] = useState<'light' | 'dark'>(
    () => (document.documentElement.dataset.theme as 'light' | 'dark') ?? 'light'
  )

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    localStorage.setItem('ralph.theme', theme)
  }, [theme])

  return { theme, toggle: () => setTheme((t) => (t === 'dark' ? 'light' : 'dark')) }
}

export const AppShell = () => {
  // Read inside the body, never captured at module scope — a frozen capture re-renders the
  // rail in the previous language with no error anywhere to show for it (PRD-27 F-3).
  const fmt = useI18n()
  const t = fmt.t.shell
  const session = useSession()
  const conversations = useConversations()
  const updateConversation = useUpdateConversation()
  const deleteConversation = useDeleteConversation()
  const approvals = useApprovals()
  const logout = useLogout()
  const navigate = useNavigate()
  const location = useLocation()
  const { id: activeId } = useParams<{ id: string }>()
  const { theme, toggle } = useTheme()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [railOpen, setRailOpen] = useState(false)
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null)
  const rail = useRef<HTMLElement>(null)

  const user = session.data?.user
  const roles = user?.roles ?? []
  const isAdmin = roles.includes('admin')
  const isOperator = roles.includes('operator') || isAdmin
  const isGovernance = isOperator || roles.includes('auditor')
  const pendingApprovals = approvals.data?.items.length ?? 0
  // Administrators decide sharing requests (PRD-26 D-29); the count is theirs alone.
  const sharingQueue = useSharingQueue('pending', isAdmin)
  const pendingSharing = sharingQueue.data?.items.length ?? 0
  const onHome = location.pathname === '/'

  /*
   * The global shortcuts are bound once and reach the current handler through a ref.
   *
   * The handler reads `location`, which is a new object on every route change, so a dependency
   * on it would tear down and re-add the window listener on every navigation. Same reasoning as
   * `focus-trap.ts`.
   */
  const goHome = () => navigate('/', { replace: onHome })
  const goHomeRef = useRef(goHome)
  useEffect(() => {
    goHomeRef.current = goHome
  })

  // Focus that would fall to the page body when its control leaves the page goes to the heading of
  // the section it was in instead (`focus-rescue.ts`, GRP-103). Once, for every screen in the shell.
  useEffect(() => installFocusRescue(document), [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target
      // A slash inside the composer is a slash. Anything typed into is left alone.
      const typing =
        target instanceof HTMLElement &&
        (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))

      // Both shortcuts are gated on `typing`, and ⌘K especially: Ctrl+K inside a text field is
      // the standard "kill to end of line" binding, so swallowing it to open a *new*
      // conversation would throw away the message someone was part-way through writing.
      if (typing) return

      // Nothing may mount over an open dialog. Every dialog traps Tab and Escape on its own
      // node, but a window-level shortcut bypasses that entirely — and two live focus traps
      // over an unfinished consent step is worse than a shortcut that occasionally declines.
      //
      // An open menu counts. The agent picker puts focus on a button rather than a field, so
      // `typing` is false there and a bare `/` would open the palette on top of it — two
      // overlays, one of which nobody asked for.
      if (document.querySelector('[role="dialog"], [role="menu"]')) return

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        goHomeRef.current()
        return
      }
      if (event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault()
        setPaletteOpen(true)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  // The drawer is a navigation surface; leaving it open over the screen it just navigated to
  // hides the thing the user asked for. The path is a trigger rather than a value the body
  // reads, which is the one shape this lint rule cannot tell apart from a mistake.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the route change IS the event
  useEffect(() => {
    setRailOpen(false)
  }, [location.pathname])

  const items = conversations.data?.items.filter((c) => !c.archived) ?? []
  const groups = groupConversations(items, Date.now())

  return (
    <HeaderSlotProvider target={headerSlot}>
      {/*
        `overflow-clip`, not `overflow-hidden`, here and on the stage below — and the difference
        is the whole point. `hidden` clips what it paints but is still a scroll container:
        nothing a person does can scroll it, yet a focus, a `scrollIntoView` or a `#fragment`
        can, and the browser will to reveal anything it believes lies outside the box. A frame
        scrolled that way shows its own background and nothing else — the header, the page and
        the rail all intact in the DOM, none of them on screen, and no error to go looking for.
        `clip` clips the same pixels and cannot be scrolled by anything, so the frame stays
        where it is whatever ends up inside it. (What put something outside the box in the
        first place is a separate rule, in `surfaces.css`: a scroller contains what it scrolls.)
      */}
      <div className="relative flex h-dvh overflow-clip bg-desk text-fg">
        <Desk />

        {/*
          The desk shows through the gap and around the edge. The insets are `max()`-ed
          against the safe area so the panels clear a notch or a home indicator on a phone
          held sideways — the viewport is already `viewport-fit=cover`, which means nothing
          protects them by default.
        */}
        <div
          className={clsx(
            'relative flex min-h-0 min-w-0 flex-1 gap-2 pt-2 sm:gap-3 sm:pt-3',
            'pl-[max(0.5rem,env(safe-area-inset-left))] pr-[max(0.5rem,env(safe-area-inset-right))] pb-[max(0.5rem,env(safe-area-inset-bottom))]',
            'sm:pl-[max(0.75rem,env(safe-area-inset-left))] sm:pr-[max(0.75rem,env(safe-area-inset-right))] sm:pb-[max(0.75rem,env(safe-area-inset-bottom))]'
          )}
        >
          {/* Mounted only while the drawer is open, which is what lets the trap be a hook at
              all — the alternative is calling one conditionally. */}
          {railOpen && <RailTrap container={rail} onClose={() => setRailOpen(false)} />}

          {/* The drawer's backdrop. Only below `lg`, where the rail overlays the stage. */}
          {railOpen && (
            <button
              type="button"
              aria-label={t.closeMenu}
              onClick={() => setRailOpen(false)}
              className="fixed inset-0 z-30 bg-[var(--scrim)]/40 backdrop-blur-[1px] lg:hidden"
            />
          )}

          {/*
            Below `lg` this is a modal drawer, so it gets what every other overlay in the app
            gets: focus moves into it on open, Tab wraps inside it, Escape closes it, and focus
            returns to the menu button. Without that a phone user opened the menu and their
            focus stayed behind it — Tab walked the stage they could no longer see.

            At `lg` and up the same element is an ordinary column in the layout and the trap is
            not mounted, because trapping focus in a permanently-visible sidebar would be a bug
            of its own.
          */}
          <nav
            ref={rail}
            id="workspace-rail"
            aria-label={t.rail}
            // No `aria-modal`, and no `role="dialog"` either. The element stays a navigation
            // landmark — both browser suites use it as the sign-in-completed signal — and
            // `aria-modal` is not a valid attribute on that role. The drawer still behaves
            // modally where it counts: focus moves in, Tab wraps, Escape closes.
            tabIndex={railOpen ? -1 : undefined}
            className={clsx(
              'glass-rail z-40 flex-col rounded-panel',
              // The `lg` rules are unconditional so a drawer left open while the window is
              // widened snaps back into the layout instead of floating over it.
              'lg:static lg:inset-auto lg:flex lg:w-[246px] lg:shrink-0 lg:shadow-[var(--glass)]',
              railOpen ? 'fixed inset-y-2 left-2 flex w-[280px] max-w-[86vw] shadow-frame' : 'hidden'
            )}
          >
            <div className="flex items-center gap-2.5 px-4 pb-3 pt-4">
              {/*
                The wordmark is the way home, which is the convention every product on the web
                shares and the one this app was missing entirely — below `lg` the rail is the
                only navigation surface there is, so without this a phone had no route back to
                the composer at all.

                The colour is not restated on hover: `a { color: var(--primary) }` in the base
                layer would turn the wordmark violet, so the link sets the ink it inherits.
                That matters more here than it reads — a customer's logotype paints its
                letterforms in `currentColor` so it survives both themes, which means this
                link's own ink is what draws somebody else's logo.
              */}
              <Link
                to="/"
                aria-label={t.homeLink(brand.productName)}
                className="-mx-1 flex min-w-0 items-center gap-2.5 rounded-chip px-1 py-0.5 text-fg transition-opacity duration-200 hover:text-fg hover:opacity-75"
              >
                <BrandLockup height={25} />
              </Link>
              <span
                className="ml-auto inline-flex items-center gap-1 rounded-full border border-rule px-[7px] py-[2.5px] font-mono text-[9px] uppercase tracking-[0.1em] text-fg-subtle"
                title={t.onPremTitle}
              >
                <Lock size={9} className="text-grounded" aria-hidden />
                {t.onPrem}
              </span>
              <IconButton label={t.closeMenu} size={25} onClick={() => setRailOpen(false)} className="lg:hidden">
                <X size={13} aria-hidden />
              </IconButton>
            </div>

            <div className="px-3 pb-3">
              {/*
                A link, because it goes to a route: that gives it the right role, middle-click
                and open-in-new-tab, and `aria-current` from `NavLink` for free — so from home
                the primary action reads as "you are already here" instead of as a control that
                does nothing. `end` keeps it from matching every route under `/`.

                `replace` while already on home so the CTA cannot fill the back stack with
                copies of the same screen. React Router still mints a new `location.key` for
                that navigation, which is what home listens to in order to put the cursor back
                in its composer.

                The shortcut is announced with `aria-keyshortcuts` and the printed key is
                hidden from the accessibility tree. Left visible it joins the accessible name
                — the control announces as "New conversation K", which is gibberish to a screen
                reader and ambiguous to any locator matching on the name.
              */}
              <NavLink
                to="/"
                end
                replace={onHome}
                title={t.newConversationTitle}
                aria-keyshortcuts="Meta+K Control+K"
                className={({ isActive }) =>
                  clsx(
                    'flex h-[38px] w-full items-center gap-2.5 rounded-tile border px-3 text-[13.5px] font-medium tracking-[-0.012em] text-fg shadow-glass',
                    'transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-px hover:text-fg hover:shadow-lift',
                    isActive ? 'border-primary bg-card-solid' : 'border-rule-strong bg-card-solid'
                  )
                }
              >
                <span
                  aria-hidden
                  className="flex h-[19px] w-[19px] shrink-0 items-center justify-center rounded-[7px] bg-[image:var(--irid)] shadow-[var(--irid-glow)]"
                >
                  <Plus size={11} strokeWidth={2.6} className="text-white" />
                </span>
                {t.newConversation}
                <kbd
                  aria-hidden
                  className="ml-auto hidden items-center gap-px rounded-[5px] border border-rule px-1 py-px font-mono text-[9.5px] font-normal text-fg-subtle sm:flex"
                >
                  <Command size={8} />K
                </kbd>
              </NavLink>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
              {conversations.isLoading && items.length === 0 ? (
                <p className="px-2 py-4 text-[12.5px] leading-relaxed text-fg-subtle">
                  <span className="sr-only">{t.loadingConversations}</span>
                  <span aria-hidden>{fmt.t.common.loading}</span>
                </p>
              ) : items.length === 0 ? (
                <p className="px-2 py-4 text-[12.5px] leading-relaxed text-fg-subtle">{t.noConversations}</p>
              ) : (
                groups.map((group) => (
                  <div key={group.key} className="mb-3 last:mb-0">
                    <div className="flex items-center gap-1.5 px-2 pb-1 pt-1.5">
                      <span className="eyebrow">{t.conversationGroups[group.key]}</span>
                      <span className="h-px flex-1 bg-rule" />
                      <span className="readout text-fg-subtle">{fmt.formatCount(group.items.length)}</span>
                    </div>
                    <ul className="space-y-px">
                      {group.items.map((conversation) => {
                        const isActive = activeId === conversation.id
                        return (
                          <li key={conversation.id} className="group relative">
                            <NavLink
                              to={`/c/${conversation.id}`}
                              className={clsx(
                                'block rounded-chip border px-2 py-1.5 pr-16 transition-[background-color,border-color,box-shadow] duration-200',
                                isActive ? 'border-rule bg-card-solid shadow-glass' : 'border-transparent hover:bg-card'
                              )}
                            >
                              <span className="flex items-center gap-2">
                                <span
                                  aria-hidden
                                  className={clsx(
                                    'flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-[6px]',
                                    isActive ? 'bg-primary-soft text-primary' : 'text-fg-subtle'
                                  )}
                                >
                                  {conversation.pinned ? <Pin size={10} /> : <FileText size={10} />}
                                </span>
                                <span
                                  className={clsx(
                                    'truncate text-[12.5px] leading-snug tracking-[-0.012em]',
                                    isActive ? 'font-semibold text-fg' : 'text-fg-muted'
                                  )}
                                >
                                  {conversation.title}
                                </span>
                              </span>
                              <span className="readout mt-0.5 block pl-[25px] text-fg-subtle">
                                {fmt.formatAgo(conversation.updatedAt)}
                                {/*
                                  Three decimals rather than the shared `formatCost`, which is
                                  this readout's own spec: a conversation is usually worth
                                  tenths of a cent and two decimals would show every row as
                                  $0.00. Only the separator and the symbol placement follow
                                  the reader — the currency stays USD, because that is what
                                  the model providers bill in (PRD-27 non-goals).
                                */}
                                {conversation.totalCostUsd > 0 &&
                                  ` · ${fmt.formatNumber(conversation.totalCostUsd, {
                                    style: 'currency',
                                    currency: 'USD',
                                    minimumFractionDigits: 3,
                                    maximumFractionDigits: 3
                                  })}`}
                              </span>
                            </NavLink>

                            {/*
                              Visually revealed on hover, but always in the tab order and
                              always revealed on focus. Hiding them with `display:none` until
                              hover made pinning and deleting a conversation reachable by
                              pointer only — there was no keyboard path to either at all.
                            */}
                            <div className="absolute right-1.5 top-1.5 flex items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                              <IconButton
                                label={
                                  conversation.pinned
                                    ? t.unpinConversation(conversation.title)
                                    : t.pinConversation(conversation.title)
                                }
                                size={22}
                                stopPropagation
                                onClick={() =>
                                  updateConversation.mutate({ id: conversation.id, pinned: !conversation.pinned })
                                }
                              >
                                {conversation.pinned ? <PinOff size={11} aria-hidden /> : <Pin size={11} aria-hidden />}
                              </IconButton>
                              <IconButton
                                label={t.deleteConversation(conversation.title)}
                                size={22}
                                tone="alert"
                                stopPropagation
                                onClick={() => {
                                  deleteConversation.mutate(conversation.id)
                                  if (activeId === conversation.id) navigate('/')
                                }}
                              >
                                <Trash2 size={11} aria-hidden />
                              </IconButton>
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                ))
              )}
            </div>

            <div className="flex flex-col gap-px border-t border-rule px-2 py-2">
              {/*
                Agents leads the block and is ungated (AGM-001). It used to sit last behind an
                admin check, which was the visible half of F-10: below operator, nobody on this
                deployment could see that agents existed at all. The gallery decides per viewer
                what is in it; the admin studio moved behind /agents/studio.
              */}
              <SideLink to="/agents" icon={<Bot size={15} />} label={t.nav.agents} />
              <SideLink to="/knowledge" icon={<BookOpen size={15} />} label={t.nav.knowledge} />
              <SideLink to="/memory" icon={<Brain size={15} />} label={t.nav.memory} />
              {/*
                Groups is for everyone (PRD-26 D-28): anybody may find a group and see who to ask
                to join it, and members and managers see what it gives access to. It takes the
                people glyph; People — the governance plane about accounts — moves to the ID card.
              */}
              <SideLink to="/groups" icon={<UsersRound size={15} />} label={t.nav.groups} />
              {(isOperator || isGovernance) && <span aria-hidden className="mx-2 my-1 h-px bg-rule" />}
              {isOperator && <SideLink to="/activity" icon={<Activity size={15} />} label={t.nav.activity} />}
              {(isOperator || isAdmin) && (
                <SideLink
                  to="/governance"
                  icon={<ShieldCheck size={15} />}
                  label={t.nav.governance}
                  count={
                    pendingSharing > 0
                      ? {
                          display: fmt.formatCount(pendingSharing),
                          label: t.sharingWaiting(fmt, pendingSharing)
                        }
                      : undefined
                  }
                />
              )}
              {/*
                People is a governance surface, not a content one. The page refuses the same way
                server-side; this only decides whether a link that would 403 is offered at all.
                (PRD-21 PPL-006.)
              */}
              {isGovernance && <SideLink to="/people" icon={<IdCard size={15} />} label={t.nav.people} />}
              {pendingApprovals > 0 && (
                <div className="mt-1 px-2 py-1">
                  <Badge tone="alert">{t.approvalsWaiting(fmt, pendingApprovals)}</Badge>
                </div>
              )}
            </div>

            {/*
              Who you are on top; the language, the theme and the way out underneath.

              This was one row of four things, and the language switcher does not fit beside
              them: the rail is 246px, the display name already truncates, and a Portuguese
              role label ("Administradora") is longer than the English one the row was
              measured against. Stacking is not a concession to the switcher — it is what
              stops the name being cut to nothing on the deployment that needs the switcher
              most (PRD-27 F-21).

              The switcher sits beside the theme toggle because they are the same kind of
              control (the PRD's own placement): a preference this browser remembers,
              changed in one tap, its current state visible without opening anything.
            */}
            <div className="mx-2 mb-2 flex flex-col gap-2 rounded-card border border-rule bg-card px-2.5 py-2">
              <div className="flex items-center gap-2.5">
                <span
                  aria-hidden
                  className="flex h-[27px] w-[27px] shrink-0 items-center justify-center rounded-full bg-[image:var(--irid)] text-[9.5px] font-semibold tracking-[0.02em] text-white shadow-[var(--irid-glow)]"
                >
                  {user?.avatarInitials ?? '··'}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12px] font-semibold tracking-[-0.012em]">
                    {user?.displayName}
                  </span>
                  {/* A word now ("Administrator"), not the enum, so the sans face: the mono one
                      was for codes, and at this width it cut the word short. */}
                  <span className="flex items-center gap-1 truncate text-[11.5px] text-fg-subtle">
                    <ShieldCheck size={9} aria-hidden className="shrink-0" />
                    {highestRoleLabel(fmt.t.people, roles)}
                  </span>
                </span>
              </div>
              <div className="flex items-center gap-1">
                <LanguageSwitcher />
                <IconButton
                  label={theme === 'dark' ? t.useLightTheme : t.useDarkTheme}
                  size={25}
                  className="ml-auto"
                  onClick={toggle}
                  stopPropagation
                >
                  {theme === 'dark' ? <Sun size={13} aria-hidden /> : <Moon size={13} aria-hidden />}
                </IconButton>
                <IconButton
                  label={t.signOut}
                  size={25}
                  stopPropagation
                  onClick={async () => {
                    await logout.mutateAsync()
                    navigate('/login')
                  }}
                >
                  <LogOut size={13} aria-hidden />
                </IconButton>
              </div>
            </div>
          </nav>

          {/* A frame, never a scroller: see `overflow-clip` at the top of this tree. */}
          <section className="glass flex min-w-0 flex-1 flex-col overflow-clip rounded-panel">
            <header className="flex items-center gap-2 border-b border-rule px-3 py-2.5 sm:gap-3 sm:px-4">
              <IconButton
                label={t.openMenu}
                size={33}
                aria-expanded={railOpen}
                aria-controls="workspace-rail"
                onClick={() => setRailOpen(true)}
                className="lg:hidden"
              >
                <Menu size={14} aria-hidden />
              </IconButton>

              <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                aria-keyshortcuts="/"
                className="flex h-[33px] cursor-text items-center gap-2 rounded-full border border-rule-strong bg-card px-3 text-left text-fg-muted transition-[border-color,background-color,box-shadow] duration-200 hover:border-primary hover:bg-card-solid hover:shadow-glass sm:min-w-[292px]"
              >
                <Search size={13} className="shrink-0" aria-hidden />
                <span className="hidden text-[12.5px] tracking-[-0.008em] sm:inline">{t.palette.placeholder}</span>
                <span className="sr-only sm:hidden">{t.palette.placeholder}</span>
                <kbd
                  aria-hidden
                  className="ml-auto hidden rounded-[5px] border border-rule px-1.5 py-px font-mono text-[9.5px] font-normal text-fg-subtle sm:block"
                >
                  /
                </kbd>
              </button>

              {/* Whatever the current route wants in the top bar — see `header-slot.tsx`. */}
              <div ref={setHeaderSlot} className="flex min-w-0 flex-1 items-center justify-end gap-2" />

              <NotificationsBell enabled={Boolean(user)} />
            </header>

            <main className="flex min-h-0 min-w-0 flex-1 flex-col">
              <Outlet />
            </main>
          </section>
        </div>

        {paletteOpen && (
          <CommandPalette
            onClose={() => setPaletteOpen(false)}
            canSeeActivity={isOperator}
            canSeeGovernance={isOperator || isAdmin}
            canSeePeople={isGovernance}
          />
        )}
      </div>
    </HeaderSlotProvider>
  )
}

/**
 * The drawer's focus trap, as a component so the hook is never called conditionally.
 *
 * Renders nothing. Mounting installs the trap on the rail and moves focus into it; unmounting
 * removes the listener and hands focus back to whatever opened it — which is the menu button,
 * because that is what had focus when this mounted.
 */
const RailTrap = ({ container, onClose }: { container: RefObject<HTMLElement | null>; onClose: () => void }) => {
  useFocusTrap(container, { onEscape: onClose })
  return null
}

const SideLink = ({
  to,
  icon,
  label,
  count
}: {
  to: string
  icon: ReactNode
  label: string
  /**
   * A number of things waiting there, with its words for a screen reader. Both arrive
   * already formatted: `SideLink` is presentation and must not decide how a number reads.
   */
  count?: { display: string; label: string } | undefined
}) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      clsx(
        'flex items-center gap-2.5 rounded-chip px-2.5 py-[7px] text-[12.5px] tracking-[-0.012em] transition-[background-color,color,box-shadow] duration-200',
        isActive
          ? 'bg-card-solid font-semibold text-fg shadow-glass [&_svg]:text-primary'
          : 'text-fg-muted hover:bg-card hover:text-fg [&_svg]:text-fg-subtle'
      )
    }
  >
    {icon}
    {label}
    {count && (
      <>
        <span
          aria-hidden
          className="ml-auto inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-primary px-1 font-mono text-[9.5px] font-semibold text-white"
        >
          {count.display}
        </span>
        <span className="sr-only">, {count.label}</span>
      </>
    )}
  </NavLink>
)
